import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import type { components } from '../../api/schema';
import { useAuth } from '../../auth/AuthProvider';
import {
  Badge,
  Button,
  Card,
  Drawer,
  Field,
  Icon,
  Input,
  Modal,
  PageHeader,
  Select,
  Table,
  type Tone,
} from '../../design';
import { useToast } from '../../design/Toast';
import { fmtDateTime, fmtRelative } from '../../lib/format';
import { useChannels } from '../automations/AutomationsPage';
import s from '../operations/operations.module.css';
import { useOrgOptions } from '../users/UsersPage';

type Endpoint = components['schemas']['WebhookEndpoint'];
type Delivery = components['schemas']['WebhookDelivery'];
type EventType = components['schemas']['WebhookEventType'];
type ApiToken = components['schemas']['ApiToken'];
type Scope = components['schemas']['ApiTokenScope'];

const SCOPE_LABEL: Record<Scope, string> = {
  fleet: 'Lockere (citire)',
  configure: 'Configurare lockere',
  hardware: 'Controler și plăci',
  operations: 'Operațiuni',
  keys: 'Chei de acces',
  documents: 'Documente',
  alerts: 'Alerte (citire, preluare)',
  resolve: 'Rezolvare alerte',
  reports: 'Rapoarte',
};

const DELIVERY_TONE: Record<Delivery['status'], { label: string; tone: Tone }> = {
  queued: { label: 'În coadă', tone: 'blue' },
  delivered: { label: 'Livrată', tone: 'green' },
  failed: { label: 'Eșuată', tone: 'red' },
  dead: { label: 'Abandonată', tone: 'red' },
};

export const GUIDE_PATH = 'docs/2026-09-12-ghid-integrari.md';

/** Integrări & API (spec §4.3): canale, webhook-uri semnate, tokenuri API, ghiduri PMS / rent-a-car / ERP. */
export function IntegrationsPage() {
  const { user, has } = useAuth();
  const orgs = useOrgOptions();
  const [orgChoice, setOrgChoice] = useState('');
  const orgId = user?.orgId ?? (orgChoice || orgs.data?.items[0]?.id || '');
  const channels = useChannels();
  const canEdit = has('integrations');

  return (
    <>
      <PageHeader
        title="Integrări & API"
        subtitle="Conectori separați, permisiuni explicite și livrare idempotentă a evenimentelor."
      />
      {user?.orgId === null && (
        <Select
          value={orgId}
          onChange={(e) => setOrgChoice(e.target.value)}
          style={{ maxWidth: 280, marginBottom: 16 }}
          aria-label="Organizație"
        >
          {orgs.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </Select>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: 16,
          marginBottom: 16,
        }}
      >
        <ChannelCard
          title="E-mail tranzacțional"
          text="Invitații, coduri de acces și confirmări de operațiune."
          enabled={channels.data?.emailEnabled}
        />
        <ChannelCard
          title="SMS (SMSO)"
          text="Coduri de ridicare, reamintiri și alerte operaționale."
          enabled={channels.data?.smsEnabled}
        />
      </div>
      {orgId && <WebhooksSection orgId={orgId} canEdit={canEdit} />}
      {orgId && <TokensSection orgId={orgId} canEdit={canEdit} />}
      <Card>
        <h2>Ghiduri de integrare</h2>
        <p className="muted small">
          PMS-urile de aparthotel, sistemele rent-a-car și ERP-urile se conectează prin aceleași
          două mecanisme: un token API cu scopuri limitate pentru a crea operațiuni și a citi
          starea, și un webhook semnat pentru a primi evenimentele. Pașii, exemplele și verificarea
          semnăturii sunt în <span className="mono">{GUIDE_PATH}</span> din depozit.
        </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 12,
            marginTop: 12,
          }}
        >
          <GuideCard
            title="PMS / aparthotel"
            text="La check-in PMS-ul creează operațiunea de predare a cheii și primește operation.ready / operation.completed."
          />
          <GuideCard
            title="Rent-a-car"
            text="Contractul creează operațiunea; returnarea cheii se confirmă prin operation.completed și access-grant.used."
          />
          <GuideCard
            title="ERP / click & collect"
            text="Comanda devine operațiune cu referința ei; ERP-ul primește operation.ready când coletul e în compartiment."
          />
        </div>
      </Card>
    </>
  );
}

function ChannelCard({
  title,
  text,
  enabled,
}: {
  title: string;
  text: string;
  enabled: boolean | undefined;
}) {
  return (
    <Card>
      <div className="flex between">
        <h3>{title}</h3>
        {enabled === undefined ? (
          <Badge tone="neutral">…</Badge>
        ) : enabled ? (
          <Badge tone="green">activ</Badge>
        ) : (
          <Badge tone="neutral">dezactivat</Badge>
        )}
      </div>
      <p className="muted small">{text}</p>
      <Link to="/setari" className="small">
        Configurează în Setări → Canale
      </Link>
    </Card>
  );
}

function GuideCard({ title, text }: { title: string; text: string }) {
  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: 14 }}>
      <strong>{title}</strong>
      <p className="small muted" style={{ marginTop: 4 }}>
        {text}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------- webhooks

function WebhooksSection({ orgId, canEdit }: { orgId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const list = useQuery({
    queryKey: ['webhook-endpoints', orgId],
    queryFn: () => unwrap(api.GET('/webhook-endpoints', { params: { query: { orgId } } })),
  });
  const catalog = useQuery({
    queryKey: ['webhook-events'],
    queryFn: () => unwrap(api.GET('/webhook-events')),
    staleTime: 300_000,
  });
  const [editing, setEditing] = useState<Endpoint | 'new' | null>(null);
  const [secret, setSecret] = useState<{ title: string; value: string } | null>(null);
  const [deliveriesFor, setDeliveriesFor] = useState<Endpoint | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['webhook-endpoints'] });

  const ping = useMutation({
    mutationFn: (e: Endpoint) =>
      unwrap(api.POST('/webhook-endpoints/{id}/ping', { params: { path: { id: e.id } } })),
    onSuccess: (d) => {
      toast(
        d.status === 'delivered'
          ? 'Ping livrat.'
          : `Ping: ${DELIVERY_TONE[d.status].label} (${d.lastError ?? 'vezi livrările'})`,
        d.status === 'delivered' ? 'success' : 'error',
      );
      refresh();
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const rotate = useMutation({
    mutationFn: (e: Endpoint) =>
      unwrap(api.POST('/webhook-endpoints/{id}/rotate-secret', { params: { path: { id: e.id } } })),
    onSuccess: (r) => {
      setSecret({ title: `Secret nou pentru „${r.name}”`, value: r.secret });
      refresh();
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const remove = useMutation({
    mutationFn: (e: Endpoint) =>
      unwrap(api.DELETE('/webhook-endpoints/{id}', { params: { path: { id: e.id } } })),
    onSuccess: () => {
      toast('Webhook-ul a fost șters.', 'success');
      refresh();
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const toggle = useMutation({
    mutationFn: (e: Endpoint) =>
      unwrap(
        api.PATCH('/webhook-endpoints/{id}', {
          params: { path: { id: e.id } },
          body: { enabled: !e.enabled },
        }),
      ),
    onSuccess: refresh,
    onError: (e) => toast(errorMessage(e), 'error'),
  });

  return (
    <Card tight style={{ marginBottom: 16 }}>
      <div className="flex between" style={{ padding: 16 }}>
        <div>
          <h2>Webhook-uri</h2>
          <p className="small muted">
            POST JSON semnat cu <span className="mono">X-SmartLocker-Signature</span>; reîncercări
            cu backoff, apoi reluare manuală din jurnal.
          </p>
        </div>
        {canEdit && (
          <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
            Webhook nou
          </Button>
        )}
      </div>
      <Table<Endpoint>
        columns={[
          {
            key: 'name',
            header: 'Nume / URL',
            render: (e) => (
              <>
                <strong>{e.name}</strong>
                <div
                  className="small muted mono"
                  style={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {e.url}
                </div>
              </>
            ),
          },
          {
            key: 'events',
            header: 'Evenimente',
            render: (e) => (
              <span className="small">
                {e.events.length === (catalog.data?.length ?? 16) ? 'toate' : e.events.join(', ')}
              </span>
            ),
          },
          {
            key: 'status',
            header: 'Stare',
            render: (e) => (
              <>
                <Badge tone={e.enabled ? 'green' : 'neutral'}>
                  {e.enabled ? 'activ' : 'oprit'}
                </Badge>
                {e.lastStatus && (
                  <div className="small muted">
                    ultima: {e.lastStatus} · {fmtRelative(e.lastDeliveryAt)}
                  </div>
                )}
              </>
            ),
          },
          {
            key: 'actions',
            header: '',
            align: 'right',
            render: (e) => (
              <span className="flex" style={{ justifyContent: 'flex-end' }}>
                <Button small onClick={() => setDeliveriesFor(e)}>
                  Livrări
                </Button>
                {canEdit && (
                  <>
                    <Button
                      small
                      onClick={() => ping.mutate(e)}
                      disabled={ping.isPending || !e.enabled}
                    >
                      Ping
                    </Button>
                    <Button small onClick={() => setEditing(e)}>
                      Configurează
                    </Button>
                    <Button small variant="ghost" onClick={() => toggle.mutate(e)}>
                      {e.enabled ? 'Oprește' : 'Pornește'}
                    </Button>
                    <Button
                      small
                      variant="ghost"
                      onClick={() => rotate.mutate(e)}
                      title="Generează un secret nou"
                    >
                      Rotire secret
                    </Button>
                    <Button
                      small
                      variant="danger"
                      icon="trash"
                      aria-label={`Șterge ${e.name}`}
                      onClick={() => remove.mutate(e)}
                    />
                  </>
                )}
              </span>
            ),
          },
        ]}
        rows={list.data ?? []}
        rowKey={(e) => e.id}
        empty={
          list.isLoading
            ? 'Se încarcă…'
            : list.error
              ? errorMessage(list.error)
              : 'Niciun webhook. Adaugă unul ca să primești evenimentele.'
        }
      />
      {editing && (
        <WebhookDialog
          orgId={orgId}
          endpoint={editing === 'new' ? null : editing}
          catalog={catalog.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={(created) => {
            setEditing(null);
            refresh();
            if (created)
              setSecret({
                title: `Secretul webhook-ului „${created.name}”`,
                value: created.secret,
              });
          }}
        />
      )}
      {secret && (
        <SecretReveal
          title={secret.title}
          value={secret.value}
          hint="Semnează antetul X-SmartLocker-Signature. Se afișează o singură dată; dacă se pierde, folosește „Rotire secret”."
          onClose={() => setSecret(null)}
        />
      )}
      {deliveriesFor && (
        <DeliveriesDrawer
          endpoint={deliveriesFor}
          canEdit={canEdit}
          onClose={() => setDeliveriesFor(null)}
        />
      )}
    </Card>
  );
}

function WebhookDialog({
  orgId,
  endpoint,
  catalog,
  onClose,
  onSaved,
}: {
  orgId: string;
  endpoint: Endpoint | null;
  catalog: { type: EventType; description: string }[];
  onClose: () => void;
  onSaved: (created: components['schemas']['WebhookEndpointCreated'] | null) => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(endpoint?.name ?? '');
  const [url, setUrl] = useState(endpoint?.url ?? 'https://');
  const [events, setEvents] = useState<EventType[]>(endpoint?.events ?? []);
  const save = useMutation({
    mutationFn: async () => {
      if (endpoint) {
        await unwrap(
          api.PATCH('/webhook-endpoints/{id}', {
            params: { path: { id: endpoint.id } },
            body: { name, url, events },
          }),
        );
        return null;
      }
      return unwrap(api.POST('/webhook-endpoints', { body: { orgId, name, url, events } }));
    },
    onSuccess: (created) => {
      toast(endpoint ? 'Webhook-ul a fost actualizat.' : 'Webhook-ul a fost creat.', 'success');
      onSaved(created);
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const toggle = (t: EventType) =>
    setEvents(events.includes(t) ? events.filter((x) => x !== t) : [...events, t]);
  return (
    <Modal
      title={endpoint ? `Webhook: ${endpoint.name}` : 'Webhook nou'}
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Renunță</Button>
          <Button
            variant="primary"
            onClick={() => save.mutate()}
            busy={save.isPending}
            disabled={name.trim().length < 2 || !url.startsWith('http') || events.length === 0}
          >
            {endpoint ? 'Salvează' : 'Creează și arată secretul'}
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="Nume" htmlFor="wh-name">
          <Input
            id="wh-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
          />
        </Field>
        <Field
          label="URL"
          hint="https:// obligatoriu (http://localhost doar în dezvoltare); adresele private sunt refuzate"
          htmlFor="wh-url"
        >
          <Input
            id="wh-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            maxLength={2048}
          />
        </Field>
        <Field label="Evenimente" hint="Alege cel puțin unul">
          <div className="flex">
            <Button small type="button" onClick={() => setEvents(catalog.map((c) => c.type))}>
              Toate
            </Button>
            <Button small type="button" variant="ghost" onClick={() => setEvents([])}>
              Niciunul
            </Button>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 6,
              marginTop: 8,
            }}
          >
            {catalog.map((c) => (
              <label key={c.type} className="flex small" style={{ alignItems: 'flex-start' }}>
                <input
                  type="checkbox"
                  checked={events.includes(c.type)}
                  onChange={() => toggle(c.type)}
                />
                <span>
                  <span className="mono">{c.type}</span>
                  <span className="muted"> · {c.description}</span>
                </span>
              </label>
            ))}
          </div>
        </Field>
      </div>
    </Modal>
  );
}

function DeliveriesDrawer({
  endpoint,
  canEdit,
  onClose,
}: {
  endpoint: Endpoint;
  canEdit: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const query = useQuery({
    queryKey: ['webhook-deliveries', endpoint.id],
    queryFn: () =>
      unwrap(
        api.GET('/webhook-deliveries', {
          params: { query: { endpointId: endpoint.id, pageSize: 50 } },
        }),
      ),
    refetchInterval: 10_000,
  });
  const retry = useMutation({
    mutationFn: (d: Delivery) =>
      unwrap(api.POST('/webhook-deliveries/{id}/retry', { params: { path: { id: d.id } } })),
    onSuccess: (d) => {
      toast(
        d.status === 'delivered' ? 'Livrată.' : `Reluare: ${DELIVERY_TONE[d.status].label}`,
        d.status === 'delivered' ? 'success' : 'error',
      );
      void qc.invalidateQueries({ queryKey: ['webhook-deliveries', endpoint.id] });
      void qc.invalidateQueries({ queryKey: ['webhook-endpoints'] });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  return (
    <Drawer
      title={`Livrări · ${endpoint.name}`}
      onClose={onClose}
      footer={<Button onClick={onClose}>Închide</Button>}
    >
      {query.isLoading && <p className="muted">Se încarcă…</p>}
      {query.error && <p style={{ color: 'var(--red)' }}>{errorMessage(query.error)}</p>}
      <div className="stack">
        {(query.data?.items ?? []).map((d) => (
          <div key={d.id} style={{ borderBottom: '1px solid var(--line)', paddingBottom: 8 }}>
            <div className="flex between">
              <span className="mono small">{d.eventType}</span>
              <Badge tone={DELIVERY_TONE[d.status].tone}>{DELIVERY_TONE[d.status].label}</Badge>
            </div>
            <div className="small muted">
              {fmtDateTime(d.createdAt)} · {d.attempts} încercări
              {d.responseCode ? ` · HTTP ${d.responseCode}` : ''}
            </div>
            {d.lastError && (
              <div className="small" style={{ color: 'var(--red)' }}>
                {d.lastError}
              </div>
            )}
            {canEdit && (d.status === 'failed' || d.status === 'dead') && (
              <Button small onClick={() => retry.mutate(d)} busy={retry.isPending}>
                Reîncearcă
              </Button>
            )}
          </div>
        ))}
        {query.data && query.data.items.length === 0 && (
          <p className="small muted">Nicio livrare încă. Folosește „Ping”.</p>
        )}
      </div>
    </Drawer>
  );
}

// ---------------------------------------------------------------- tokenuri

function TokensSection({ orgId, canEdit }: { orgId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const list = useQuery({
    queryKey: ['api-tokens', orgId],
    queryFn: () => unwrap(api.GET('/api-tokens', { params: { query: { orgId } } })),
  });
  const scopes = useQuery({
    queryKey: ['api-token-scopes'],
    queryFn: () => unwrap(api.GET('/api-tokens/scopes')),
    staleTime: 300_000,
  });
  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState<{ title: string; value: string } | null>(null);
  const revoke = useMutation({
    mutationFn: (t: ApiToken) =>
      unwrap(api.POST('/api-tokens/{id}/revoke', { params: { path: { id: t.id } } })),
    onSuccess: () => {
      toast('Tokenul a fost revocat.', 'success');
      void qc.invalidateQueries({ queryKey: ['api-tokens'] });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  return (
    <Card tight style={{ marginBottom: 16 }}>
      <div className="flex between" style={{ padding: 16 }}>
        <div>
          <h2>Tokenuri API</h2>
          <p className="small muted">
            <span className="mono">Authorization: Bearer slk_…</span>; scopuri din permisiuni, fără
            acțiuni fizice; POST-urile cer <span className="mono">Idempotency-Key</span>.
          </p>
        </div>
        {canEdit && (
          <Button variant="primary" icon="key" onClick={() => setCreating(true)}>
            Token nou
          </Button>
        )}
      </div>
      <Table<ApiToken>
        columns={[
          {
            key: 'name',
            header: 'Nume / prefix',
            render: (t) => (
              <>
                <strong>{t.name}</strong>
                <div className="small muted mono">slk_{t.prefix}_••••</div>
              </>
            ),
          },
          {
            key: 'scopes',
            header: 'Scopuri',
            render: (t) => (
              <span className="small">
                {t.scopes.map((sc) => SCOPE_LABEL[sc] ?? sc).join(', ')}
              </span>
            ),
          },
          {
            key: 'expires',
            header: 'Expiră',
            render: (t) => (
              <span className="muted small">
                {t.expiresAt ? fmtDateTime(t.expiresAt) : 'niciodată'}
              </span>
            ),
          },
          {
            key: 'used',
            header: 'Ultima folosire',
            render: (t) => <span className="muted small">{fmtRelative(t.lastUsedAt)}</span>,
          },
          {
            key: 'status',
            header: 'Stare',
            render: (t) =>
              t.revokedAt ? (
                <Badge tone="red">revocat</Badge>
              ) : t.expiresAt && new Date(t.expiresAt) < new Date() ? (
                <Badge tone="orange">expirat</Badge>
              ) : (
                <Badge tone="green">activ</Badge>
              ),
          },
          {
            key: 'actions',
            header: '',
            align: 'right',
            render: (t) =>
              canEdit && !t.revokedAt ? (
                <Button small variant="danger" onClick={() => revoke.mutate(t)}>
                  Revocă
                </Button>
              ) : null,
          },
        ]}
        rows={list.data ?? []}
        rowKey={(t) => t.id}
        empty={
          list.isLoading
            ? 'Se încarcă…'
            : list.error
              ? errorMessage(list.error)
              : 'Niciun token emis.'
        }
      />
      {creating && (
        <TokenDialog
          orgId={orgId}
          scopes={scopes.data ?? []}
          onClose={() => setCreating(false)}
          onCreated={(t) => {
            setCreating(false);
            void qc.invalidateQueries({ queryKey: ['api-tokens'] });
            setSecret({ title: `Tokenul „${t.name}”`, value: t.token });
          }}
        />
      )}
      {secret && (
        <SecretReveal
          title={secret.title}
          value={secret.value}
          hint="Copiază-l acum în sistemul extern. Nu mai poate fi afișat; dacă se pierde, revocă-l și emite altul."
          onClose={() => setSecret(null)}
        />
      )}
    </Card>
  );
}

function TokenDialog({
  orgId,
  scopes,
  onClose,
  onCreated,
}: {
  orgId: string;
  scopes: Scope[];
  onClose: () => void;
  onCreated: (t: components['schemas']['ApiTokenCreated']) => void;
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [chosen, setChosen] = useState<Scope[]>([]);
  const [expires, setExpires] = useState('');
  const create = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api-tokens', {
          body: {
            orgId,
            name,
            scopes: chosen,
            expiresAt: expires ? new Date(expires).toISOString() : null,
          },
        }),
      ),
    onSuccess: onCreated,
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const toggle = (sc: Scope) =>
    setChosen(chosen.includes(sc) ? chosen.filter((x) => x !== sc) : [...chosen, sc]);
  return (
    <Modal
      title="Token API nou"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Renunță</Button>
          <Button
            variant="primary"
            onClick={() => create.mutate()}
            busy={create.isPending}
            disabled={name.trim().length < 2 || chosen.length === 0}
          >
            Emite tokenul
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="Nume" hint="Ex. „ERP Sincronizare comenzi”" htmlFor="tk-name">
          <Input
            id="tk-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            autoFocus
          />
        </Field>
        <Field
          label="Scopuri"
          hint="Acțiunile fizice (deschidere, aprobare, publicare) și administrarea nu sunt disponibile tokenurilor"
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {scopes.map((sc) => (
              <label key={sc} className="flex small">
                <input type="checkbox" checked={chosen.includes(sc)} onChange={() => toggle(sc)} />
                {SCOPE_LABEL[sc] ?? sc}
              </label>
            ))}
          </div>
        </Field>
        <Field label="Expiră la" hint="Gol = fără expirare; maximum 2 ani" htmlFor="tk-exp">
          <Input
            id="tk-exp"
            type="date"
            value={expires}
            onChange={(e) => setExpires(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- secret o singură dată

export function SecretReveal({
  title,
  value,
  hint,
  onClose,
}: {
  title: string;
  value: string;
  hint: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      /* selecția manuală rămâne posibilă */
    }
  };
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <Button variant="primary" onClick={onClose}>
          Am salvat valoarea
        </Button>
      }
    >
      <div className="stack">
        <div className={s.codeBox}>
          <div
            className="mono"
            aria-label="Valoarea secretă"
            style={{ wordBreak: 'break-all', fontSize: 14, userSelect: 'all' }}
          >
            {value}
          </div>
          <div style={{ marginTop: 10 }}>
            <Button icon={copied ? 'check' : 'copy'} onClick={() => void copy()}>
              {copied ? 'Copiat' : 'Copiază'}
            </Button>
          </div>
        </div>
        <div className={s.warn} role="note">
          <Icon name="alert" size={18} />
          <span>{hint}</span>
        </div>
      </div>
    </Modal>
  );
}
